/**
 * Limits Kuali imports to the award-document categories useful for review.
 *
 * Attachment type is provider metadata, but selecting categories is a workspace
 * business rule that prevents internal budgets and reports from being copied
 * merely because the API key can see them.
 */
import { Checkbox } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { KualiResearchAttachmentTypeCode } from '@/subsystems/vault/connectors/kuali-research/types'

export const KUALI_RESEARCH_ATTACHMENT_TYPES = [
  { code: '1', label: 'Initial Award' },
  { code: '2', label: 'Award Modification / Backup' },
  { code: '3', label: 'Approved Pre-Award' },
  { code: '5', label: 'Reports' },
  { code: '6', label: 'Award Budget' },
  { code: '9', label: 'Cost Sharing' },
] as const satisfies readonly {
  code: KualiResearchAttachmentTypeCode
  label: string
}[]

export const ALL_KUALI_RESEARCH_ATTACHMENT_TYPE_CODES
  = KUALI_RESEARCH_ATTACHMENT_TYPES.map(({ code }) => code)

export function KualiResearchAttachmentTypeSelection({
  selectedCodes,
  onSelectedCodesChange,
}: {
  selectedCodes: KualiResearchAttachmentTypeCode[]
  onSelectedCodesChange: (codes: KualiResearchAttachmentTypeCode[]) => void
}) {
  const toggle = (
    code: KualiResearchAttachmentTypeCode,
    checked: boolean,
  ) => {
    onSelectedCodesChange(
      checked
        ? [...selectedCodes, code]
        : selectedCodes.filter(selected => selected !== code),
    )
  }

  return (
    <fieldset className="space-y-1.5">
      <legend className="sr-only">Document types</legend>
      <div className="flex flex-wrap gap-1.5">
        {KUALI_RESEARCH_ATTACHMENT_TYPES.map(({ code, label }) => {
          const selected = selectedCodes.includes(code)
          return (
            <label
              key={code}
              className={cn(
                'flex h-[30px] cursor-pointer items-center rounded-[8px] border px-3 text-[12px] font-semibold transition-colors',
                selected
                  ? 'border-slate-800 bg-slate-800 text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
              )}
            >
              <Checkbox
                className="sr-only"
                aria-label={`${code} ${label}`}
                checked={selected}
                onCheckedChange={checked => toggle(code, checked === true)}
              />
              {label}
            </label>
          )
        })}
      </div>
      {!selectedCodes.length && (
        <p className="text-xs text-destructive">
          Select at least one attachment type.
        </p>
      )}
    </fieldset>
  )
}
